import * as React from 'react';
import moment from 'moment-timezone';
import * as queryString from 'query-string';
import _ from 'lodash';

import { Callout, ContextualMenu, DefaultButton, Dropdown, IContextualMenuProps, IDropdownOption, SearchBox, Spinner, SpinnerSize, IContextualMenuItem } from '@fluentui/react';
import { Claim, State } from '../../redux/claim/types';

import { AsyncData } from '../../redux/core';
import { ClaimDetailsList } from './ClaimDetailsList';
import { ClaimsDayRangeSelector } from './ClaimsDayRangeSelector';
import { PageHeader } from '../core/PageHeader';
import { OkCancelDialog } from '../core/OkCancelDialog';
import { ROUTES } from '../../config/routes';
import { InfoMessage } from '../core/InfoMessage';
import { NavigateFunction } from 'react-router-dom';

export interface OwnProps {
  params?: any;
  navigate: NavigateFunction;
}

export interface StateProps {
  claims: AsyncData<Claim[]>;
  claimsStatus: string[];
  claimsSearchText: string;
  optionKeyDepressed: boolean;
  claimsFromDate?: string;
  claimsToDate?: string;
}

export interface DispatchProps {
  actions: {
    onPageDidMount: (name: string) => void;
    onPageWillUnmount: (name: string) => void;
    setClaimStatus: (status: string[]) => void;
    setClaimsSearchText: (value: string) => void;
    generateClaim: (visitCode: string, ignoreOldDataCheck: boolean, forceFinalizedClaimReplacement: boolean) => void;
    submitClaim: (claimId: string) => void;
    refreshClaim: (claimId: string) => void;
    resetClaim: (claimId: string) => void;
    setClaimsFromDate: (date: string) => void;
    setClaimsToDate: (date: string) => void;
  };
}

type ClaimsProps = OwnProps & StateProps & DispatchProps;

export interface ClaimsState {
  items: Claim[];
  selectedClaim?: Claim;
  filterCalloutVisible: boolean;
}

export const CLAIM_OPTIONS: { key: string; text: string }[] = [
  { key: 'Draft', text: 'Draft' },
  { key: 'ValidationFailed', text: 'Validation Failed' },
  { key: 'Submitted', text: 'Submitted' },
  { key: 'Acknowledged', text: 'Acknowledged' },
  { key: 'Adjudication', text: 'Adjudication' },
  { key: 'MoreInfoRequired', text: 'More Info Required' },
  { key: 'Rejected', text: 'Rejected' },
  { key: 'Replaced', text: 'Replaced' },
  { key: 'PayerError', text: 'Payer Error' },
  { key: 'Finalized', text: 'Finalized' },
  { key: 'Unknown', text: 'Unknown' },
];

export class Claims extends React.Component<ClaimsProps, ClaimsState> {
  private _filterButtonElement = React.createRef<HTMLDivElement>();

  constructor(props: ClaimsProps) {
    super(props);
    this.state = {
      items: [],
      selectedClaim: undefined,
      filterCalloutVisible: false,
    };
  }

  public componentDidMount() {
    this.props.actions.onPageDidMount('Claims');
    this.setFilterValues();
  }

  public componentWillUnmount() {
    this.props.actions.onPageWillUnmount('Claims');
  }

  public render(): JSX.Element {
    return (
      <div>
        <PageHeader title="Claims" showShareButton={true} />
        <div className="flex">
          <div style={{ marginRight: 10, width: 320 }}>
            <SearchBox placeholder="Search claims" value={this.props.claimsSearchText} onChange={this.onSearchChanged} />
          </div>
          <div style={{ marginRight: 10 }}>{this.renderFilterButton()}</div>
          <div style={{ marginRight: 10 }}>{this.renderActionsButton()}</div>
          <DefaultButton iconProps={{ iconName: 'money' }} text="Fee Schedule" href={ROUTES.feeSchedule} />
          <div style={{ textAlign: 'right', flexGrow: 1 }}>{this.renderResultsCount()}</div>
          {this.renderFilterCallout()}
        </div>

        <ClaimDetailsList
          onActiveItemChanged={(item) => this.onActiveItemChanged(item)}
          onClaimActionsRequested={() => this.getMenuActionsByClaim()}
          items={this.props.claims.data || []}
        />
        {this.renderStatus()}
      </div>
    );
  }

  private onActiveItemChanged(item: Claim) {
    this.setState({ selectedClaim: item });
  }

  private renderFilterCallout(): JSX.Element {
    const options: { key: string; text: string }[] = [{ key: 'ALL', text: 'All' }, ...CLAIM_OPTIONS];

    const selectedKeys = (this.props.claimsStatus || []).slice();
    if (selectedKeys.length === options.length - 1) {
      selectedKeys.push('ALL');
    }

    const dateNow = moment().format('L');
    const date7DaysAgo = moment().subtract('7', 'day').format('L');
    const date1MonthAgo = moment().subtract('1', 'month').format('L');
    const date3MonthAgo = moment().subtract('3', 'month').format('L');
    const date1YearAgo = moment().subtract('1', 'year').format('L');
    const dateAllTime = moment('2014-01-01').format('L');

    return (
      <Callout
        role={'alertdialog'}
        gapSpace={0}
        target={this._filterButtonElement.current}
        onDismiss={this._onCalloutDismiss}
        setInitialFocus={true}
        hidden={!this.state.filterCalloutVisible}
      >
        <div style={{ width: 500 }}>
          <div style={{ padding: 10 }}>
            <Dropdown
              label="Claim status"
              style={{ width: '100%' }}
              placeholder="Select states"
              selectedKeys={selectedKeys}
              onChange={this.onChanged}
              multiSelect={true}
              options={options}
            />
          </div>
          <div style={{ paddingLeft: 10 }}>Date Range</div>
          <div style={{ padding: 10 }} className="flex">
            <DefaultButton
              text="Last week"
              toggle={date7DaysAgo === this.props.claimsFromDate}
              checked={date7DaysAgo === this.props.claimsFromDate}
              onClick={() => {
                this.setClaimsFromDate(date7DaysAgo);
                this.setClaimsToDate(dateNow);
              }}
            />
            <DefaultButton
              text="Last month"
              toggle={date1MonthAgo === this.props.claimsFromDate}
              checked={date1MonthAgo === this.props.claimsFromDate}
              onClick={() => {
                this.setClaimsFromDate(date1MonthAgo);
                this.setClaimsToDate(dateNow);
              }}
            />
            <DefaultButton
              text="Last 3 months"
              toggle={date3MonthAgo === this.props.claimsFromDate}
              checked={date3MonthAgo === this.props.claimsFromDate}
              onClick={() => {
                this.setClaimsFromDate(date3MonthAgo);
                this.setClaimsToDate(dateNow);
              }}
            />
            <DefaultButton
              text="Last year"
              toggle={date1YearAgo === this.props.claimsFromDate}
              checked={date1YearAgo === this.props.claimsFromDate}
              onClick={() => {
                this.setClaimsFromDate(date1YearAgo);
                this.setClaimsToDate(dateNow);
              }}
            />
            <DefaultButton
              text="All time"
              toggle={dateAllTime === this.props.claimsFromDate}
              checked={dateAllTime === this.props.claimsFromDate}
              onClick={() => {
                this.setClaimsFromDate(dateAllTime);
                this.setClaimsToDate(dateNow);
              }}
            />
          </div>
          <div className="flex" style={{ paddingLeft: 10, paddingBottom: 10 }}>
            <ClaimsDayRangeSelector
              actions={{
                setClaimsFromDate: this.setClaimsFromDate,
                setClaimsToDate: this.setClaimsToDate,
              }}
              claimsFromDate={this.props.claimsFromDate}
              claimsToDate={this.props.claimsToDate}
            />
          </div>
        </div>
      </Callout>
    );
  }

  private filterButtonOnClick = () => {
    this.setState({ filterCalloutVisible: !this.state.filterCalloutVisible });
  };

  private _onCalloutDismiss = (): void => {
    this.setState({
      filterCalloutVisible: false,
    });
  };

  private renderFilterButton(): JSX.Element {
    const statusFilterCount = this.props.claimsStatus ? this.props.claimsStatus.length : 0;
    const isDateFiltered = this.props.claimsFromDate !== '01/01/2014';
    const isFiltering: boolean = (statusFilterCount !== CLAIM_OPTIONS.length && statusFilterCount !== 0) || isDateFiltered;

    return (
      <div ref={this._filterButtonElement}>
        <DefaultButton allowDisabledFocus={true} iconProps={{ iconName: 'filter' }} checked={isFiltering} text="Filter" onClick={this.filterButtonOnClick} />
      </div>
    );
  }

  private renderActionsButton(): JSX.Element {
    return (
      <DefaultButton
        allowDisabledFocus={true}
        iconProps={{ iconName: 'contextMenu' }}
        text="Actions"
        menuAs={this.getActionMenu}
        menuProps={{
          items: [],
        }}
      />
    );
  }
  private getActionMenu = (menuProps: IContextualMenuProps): JSX.Element => {
    const copiedMenuProps = { ...menuProps, items: this.getMenuActionsByClaim() };
    return <ContextualMenu {...copiedMenuProps} />;
  };

  private getMenuActionsByClaim(): IContextualMenuItem[] {
    const options = [
      {
        key: 'submit',
        text: 'Submit',
        iconProps: { iconName: 'send' },
        onClick: () => {
          if (this.state.selectedClaim) {
            this.props.actions.submitClaim(this.state.selectedClaim.id);
          }
        },
      },
      {
        key: 'calendarEvent',
        text: 'Re-Generate',
        iconProps: { iconName: 'refresh' },
        onClick: () => {
          if (this.state.selectedClaim) {
            if (this.state.selectedClaim.state === State.Finalized) {
              OkCancelDialog.show({
                title: 'Re-Generate claim',
                subText: 'This claim is already Finalized.  Are you sure you want to re-generate and resubmit it?',
                onOk: () => this.props.actions.generateClaim(this.state.selectedClaim!.externalReferenceId, true, true),
              });
            } else {
              this.props.actions.generateClaim(this.state.selectedClaim.externalReferenceId, true, false);
            }
          }
        },
      },
      {
        key: 'refresh',
        text: 'Check for Updates',
        iconProps: { iconName: 'cloudDownload' },
        onClick: () => {
          if (this.state.selectedClaim) {
            this.props.actions.refreshClaim(this.state.selectedClaim.id);
          }
        },
      },
    ];
    if (this.props.optionKeyDepressed) {
      options.push({
        key: 'reset',
        text: 'Reset State',
        iconProps: { iconName: 'removeOccurrence' },
        onClick: () => {
          if (this.state.selectedClaim) {
            this.props.actions.resetClaim(this.state.selectedClaim.id);
          }
        },
      });
    }
    return options;
  }

  private renderResultsCount(): JSX.Element | null {
    if (!this.props.claims.data || this.props.claims.data.length === 0) {
      return null;
    }

    const count = this.props.claims.data.length;
    return <span style={{ fontSize: 20 }}> Results: {count}</span>;
  }

  private renderStatus(): JSX.Element | null {
    if (this.props.claims.isFetching) {
      return <Spinner style={{ marginTop: 20 }} size={SpinnerSize.large} label="Searching for you..." />;
    }

    if (this.props.claims.data && this.props.claims.data.length === 0) {
      return (
        <InfoMessage>
          <div>
            <b>Tips</b>
          </div>
          <div>
            <ul>
              <li>You can search for things like visit code, member name and Id, group name and Id, payer, and claim control number.</li>
              <li>Filter you search results by day or claim state (e.g. Draft, Rejected, Finalized) by clicking on the Filter button.</li>
              <li>You can perform actions on claims selecting a claim and clicking the actions button, or by clicking the menu icon on the right side of a claim.</li>
            </ul>
          </div>
        </InfoMessage>
      );
    }
    return null;
  }

  private onSearchChanged = (ev?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => {
    this.setClaimsSearchText(newValue || '');
  };

  private onChanged = (ev: React.FormEvent<HTMLDivElement>, item?: IDropdownOption) => {
    if (!item) {
      return;
    }
    if (item.key === 'ALL') {
      if (item.selected) {
        this.setClaimStatus(CLAIM_OPTIONS.map((i) => i.key));
      } else {
        this.setClaimStatus([]);
      }
      return;
    }
    const updatedSelectedItem = this.props.claimsStatus.slice();
    if (item.selected) {
      // add the option if it's checked
      updatedSelectedItem.push(item.key as string);
    } else {
      // remove the option if it's unchecked
      const currIndex = updatedSelectedItem.indexOf(item.key as string);
      if (currIndex > -1) {
        updatedSelectedItem.splice(currIndex, 1);
      }
    }
    this.setClaimStatus(updatedSelectedItem);
  };

  private setClaimStatus = (selectedItems: string[]) => {
    this.props.actions.setClaimStatus(selectedItems);
    this.replaceHistory('status', selectedItems);
  };

  private setClaimsSearchText = (value: string) => {
    this.props.actions.setClaimsSearchText(value);
    this.replaceHistory('search', value);
  };

  private setClaimsFromDate = (date: string) => {
    this.props.actions.setClaimsFromDate(date);
    this.replaceHistory('from', date);
  };

  private setClaimsToDate = (date: string) => {
    this.props.actions.setClaimsToDate(date);
    this.replaceHistory('to', date);
  };

  private replaceHistory(item: string, value: string | string[]) {
    const params = queryString.parse(window.location.search);
    params[item] = value;
    this.props.navigate(
      {
        pathname: ROUTES.claims,
        search: queryString.stringify(params, { arrayFormat: 'comma' }),
      },
      { replace: true }
    );
  }

  private setFilterValues() {
    const params = queryString.parse(window.location.search);

    if (params.status) {
      this.props.actions.setClaimStatus((params.status as string).split(','));
    } else {
      if (this.props.claimsStatus && this.props.claimsStatus.length > 0) {
        params.status = this.props.claimsStatus;
      } else {
        this.props.actions.setClaimStatus(CLAIM_OPTIONS.map((i) => i.key));
      }
    }

    if (params.search) {
      this.props.actions.setClaimsSearchText(params.search as string);
    } else {
      if (this.props.claimsSearchText && this.props.claimsSearchText.length > 0) {
        params.search = this.props.claimsSearchText;
      }
    }

    if (params.from) {
      this.props.actions.setClaimsFromDate(params.from as string);
    } else {
      if (this.props.claimsFromDate && this.props.claimsFromDate.length > 0) {
        params.from = this.props.claimsFromDate;
      } else {
        this.setClaimsFromDate(moment().subtract('1', 'year').format('L'));
      }
    }

    if (params.to) {
      this.props.actions.setClaimsToDate(params.to as string);
    } else {
      if (this.props.claimsToDate && this.props.claimsToDate.length > 0) {
        params.to = this.props.claimsToDate;
      }
    }

    if (!_.isEqual(params, queryString.parse(window.location.search))) {
      this.props.navigate(
        {
          pathname: ROUTES.claims,
          search: queryString.stringify(params, { arrayFormat: 'comma' }),
        },
        { replace: true }
      );
    }
  }
}
